import React, { useRef } from 'react';
import { Button, Text } from '@fragments';
import { IconArrowUpRight, IconSparkles } from '@tabler/icons-react';
import { Sparks, gsap, ambience, useSceneAnimation } from '../../components/Scene';
import { formatCredits, formatDay } from '../../data/payments';
import './styles.scss';

const DOTS = 18;
const SPARKS = [[300, 60], [250, 120], [330, 170], [60, 330], [120, 360]];

// Plays once per mount: the lit dots fill in, the indicator travels to the balance and
// the counter climbs to it. Everything ends on what React rendered (CSS + text), so
// later balance updates need no animation state; the glows keep drifting.
function build(q, { still }) {
  if (still) return;
  ambience(q);
  const fills = q('.studio-upgrade-card__dot.is-lit > i');
  const counter = q('[data-counter]')[0];
  const text = counter?.firstChild;
  const format = value => formatCredits(Math.round(value), counter.dataset.locale);
  const tally = { value: 0 };
  const duration = Math.max(fills.length * 0.06, 0.4) + 0.2;
  gsap.timeline({ delay: 0.5 })
    .from(fills, { opacity: 0, duration: 0.3, ease: 'power2.out', stagger: 0.06, clearProps: 'opacity' }, 0)
    .from(q('[data-indicator]'), { left: '0%', duration, ease: 'power1.inOut', clearProps: 'left' }, 0)
    .fromTo(tally, { value: 0 }, { value: () => Number(counter?.dataset.value || 0), duration, ease: 'power1.inOut',
      // Writes into React's own text node, and ends on its current value.
      onUpdate: () => { if (text) text.nodeValue = format(tally.value); },
      onComplete: () => { if (text) text.nodeValue = format(Number(counter.dataset.value || 0)); } }, 0);
}

// The creator's plan and balance in the sidebar, with the way up: the next plan (free →
// Standard → Pro), or credit packs on Pro. The meter weighs the balance against the
// next plan's month, or against the current month on Pro. The whole card is the action:
// its AI button is the focusable control, and every click (the button's included)
// reaches the card's handler.
export default function StudioUpgradeCard({ studio: s, onOpen }) {
  const { t } = s;
  const p = s.payments;
  const stage = useRef(null);
  const locale = s.locale.replace('_', '-');
  useSceneAnimation(stage, build);
  if (s.credits === null) return null;
  const usage = s.snapshot.usage || {};
  const available = s.credits;
  const target = p.nextPlan;
  const offer = target ? p.planInfo(target)?.monthly_credits ?? 0 : null;
  const scale = Math.max(offer ?? (p.planInfo(p.plan)?.monthly_credits ?? 0) + (usage.purchased_units ?? 0), available, 1);
  const lit = Math.round((Math.min(available, scale) / scale) * DOTS);
  const open = () => onOpen(() => (target ? p.openUpgrade(target) : p.openCredits()));
  const note = target ? t('upgradeCardOffer', { plan: t(`planName_${target}`), credits: formatCredits(offer, locale) })
    : usage.allowance_expires_at ? t('upgradeCardRenews', { date: formatDay(usage.allowance_expires_at, locale) }) : null;
  const label = `${t(`planName_${p.plan}`)} · ${t('credits', { count: available })}. ${target ? t('upgradeTo', { plan: t(`planName_${target}`) }) : t('upgradeCardBuy')}`;
  return <div ref={stage} className="studio-upgrade-card" onClick={open}>
    <span className="studio-upgrade-card__fx" aria-hidden="true">
      <span className="studio-upgrade-card__glow is-violet" data-glow />
      <span className="studio-upgrade-card__glow is-warm" data-glow />
      <span className="studio-upgrade-card__glow is-cool" data-glow />
      <Sparks points={SPARKS} />
    </span>
    <span className="studio-upgrade-card__head" aria-hidden="true">
      <Text.caption contrast={8} left={<IconSparkles />}>{t(`planName_${p.plan}`)}</Text.caption>
      <IconArrowUpRight className="studio-upgrade-card__arrow" />
    </span>
    <span className="studio-upgrade-card__value" aria-hidden="true">
      <Text.h3 contrast={10} weight={500} data-counter data-value={available} data-locale={locale}>{formatCredits(available, locale)}</Text.h3>
      <Text.caption contrast={6}>{t('upgradeCardBalance')}</Text.caption>
    </span>
    <span className="studio-upgrade-card__meter" aria-hidden="true" style={{ '--fill': `${(lit / DOTS) * 100}%` }}>
      {Array.from({ length: DOTS }, (_, i) => <span key={i} className={`studio-upgrade-card__dot ${i < lit ? 'is-lit' : ''}`} style={{ '--t': i / (DOTS - 1) }}><i /></span>)}
      <b className="studio-upgrade-card__indicator" data-indicator />
    </span>
    {note && <Text.caption className="studio-upgrade-card__note" contrast={6} aria-hidden="true">{note}</Text.caption>}
    <Button className="studio-upgrade-card__cta" color="inverse" variant="rounded" size="small" fullWidth preventLoading aria-label={label}>
      {target ? t('upgradeCardCta') : t('upgradeCardBuy')}
    </Button>
  </div>;
}
